import { useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { generateTrackingId } from '@frontend/call-intel-core';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem, Text, usePopoverMenu } from '@frontend/design-system';

type CallSearchTypeDropdownProps = {
  menuRef: React.RefObject<HTMLDivElement>;
  value: CallIntelTypes.SearchTypeEnum;
  onChange: (value: CallIntelTypes.SearchTypeEnum) => void;
};

const SEARCH_TYPE_OPTIONS = [
  {
    label: i18next.t('Summary & Transcript', { ns: 'call-intelligence' }),
    value: CallIntelTypes.SearchTypeEnum.SEARCH_BY_CONTENT,
    trackingId: 'search-type-content',
  },
  {
    label: i18next.t('Name', { ns: 'call-intelligence' }),
    value: CallIntelTypes.SearchTypeEnum.SEARCH_BY_NAME,
    trackingId: 'search-type-name',
  },
  {
    label: i18next.t('Phone Number', { ns: 'call-intelligence' }),
    value: CallIntelTypes.SearchTypeEnum.SEARCH_BY_NUMBER,
    trackingId: 'search-type-number',
  },
];

const BASE_TRACKING_ID = 'call-search-type-dropdown';

export const CallSearchTypeDropdown = ({ value, onChange, menuRef }: CallSearchTypeDropdownProps) => {
  const { t } = useTranslation('call-intelligence');
  const { getItemProps, getMenuProps, getTriggerProps } = usePopoverMenu({
    placement: 'bottom-start',
  });

  const activeOption = useMemo(
    () => SEARCH_TYPE_OPTIONS.find((option) => option.value === value) ?? SEARCH_TYPE_OPTIONS[0],
    [value]
  );

  return (
    <>
      <div css={styles.wrapper}>
        <Text color='subdued' css={{ whiteSpace: 'nowrap' }}>
          {t('Search calls by:')}
        </Text>

        <Button
          variant='tertiary'
          {...getTriggerProps()}
          css={styles.trigger}
          trackingId={generateTrackingId({
            component: BASE_TRACKING_ID,
            context: 'button-click',
          })}
        >
          <span>{activeOption.label}</span>
          <Icon name='alt-caret-down-tiny' size={8} />
        </Button>
      </div>

      <PopoverMenu css={styles.menu} {...getMenuProps()}>
        <div ref={menuRef}>
          {SEARCH_TYPE_OPTIONS.map((option, index) => (
            <PopoverMenuItem
              key={option.value}
              trackingId={generateTrackingId({
                component: BASE_TRACKING_ID,
                context: option.trackingId,
              })}
              {...getItemProps({
                index,
                onClick: () => onChange(option.value),
              })}
              active={option.value === value}
              css={{ cursor: 'pointer' }}
            >
              {option.label}
            </PopoverMenuItem>
          ))}
        </div>
      </PopoverMenu>
    </>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
    gap: var(--fabric-number-n-0-5-px);
  `,
  trigger: css`
    display: flex;
    align-items: center;
    gap: var(--fabric-number-n-1-px);
    font-size: var(--fabric-font-size-md);
    height: ${theme.spacing(3.5)};
    padding: ${theme.spacing(0.5)};
    &:focus-visible {
      box-shadow: none;
    }
  `,
  menu: css`
    width: 280px;
  `,
};
